/**
 * People Page
 * 
 * Team directory with presence status and quick actions.
 */

import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { 
  Users, 
  Search, 
  MessageSquare,
  Mail,
  Phone,
  MapPin,
  Circle,
  Loader2,
  Filter,
  LayoutGrid,
  List,
} from 'lucide-react'
import { cn, getInitials, getAvatarColor, getPresenceConfig } from '../lib/utils'
import { usersApi, dmApi, type User } from '../lib/api'
import { useTeamsStore } from '../store/teamsStore'

type ViewMode = 'grid' | 'list'
type FilterMode = 'all' | 'online' | 'offline'

export default function PeoplePage() {
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  const [viewMode, setViewMode] = useState<ViewMode>('grid')
  const [filterMode, setFilterMode] = useState<FilterMode>('all')
  const { setUsers } = useTeamsStore()
  
  // Fetch users
  const { data: users = [], isLoading } = useQuery({
    queryKey: ['teams-users'],
    queryFn: usersApi.list,
    onSuccess: setUsers,
    refetchInterval: 30000, // Refresh presence every 30s
  })
  
  // Filter users
  const filteredUsers = useMemo(() => {
    let result = users.filter(u => !u.is_self)
    
    if (filterMode === 'online') {
      result = result.filter(u => u.is_online)
    } else if (filterMode === 'offline') {
      result = result.filter(u => !u.is_online)
    }
    
    if (searchQuery) {
      const q = searchQuery.toLowerCase()
      result = result.filter(u => 
        u.name?.toLowerCase().includes(q) ||
        u.email?.toLowerCase().includes(q) ||
        u.department?.toLowerCase().includes(q)
      )
    }
    
    return result
  }, [users, searchQuery, filterMode])
  
  const onlineCount = users.filter(u => u.is_online && !u.is_self).length
  
  // Start DM
  const handleMessage = async (userId: number) => {
    try {
      const result = await dmApi.getOrCreate(userId)
      navigate(`/messages/${result.id}`)
    } catch (e) {
      console.error('Failed to start DM:', e)
    }
  }

  return (
    <div className="h-full bg-slate-50 dark:bg-slate-900">
      <div className="max-w-6xl mx-auto px-6 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-1">People</h1>
            <p className="text-slate-500 dark:text-slate-400">
              {onlineCount} online of {users.filter(u => !u.is_self).length} team members
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setViewMode('grid')}
              className={cn(
                "p-2 rounded-lg transition-colors",
                viewMode === 'grid' 
                  ? "bg-violet-100 dark:bg-violet-900/30 text-violet-600" 
                  : "text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
              )}
            >
              <LayoutGrid className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={cn(
                "p-2 rounded-lg transition-colors",
                viewMode === 'list' 
                  ? "bg-violet-100 dark:bg-violet-900/30 text-violet-600" 
                  : "text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
              )}
            >
              <List className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        {/* Search & Filter */}
        <div className="flex items-center gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name, email, or department..."
              className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:ring-2 focus:ring-violet-500 focus:border-transparent"
            />
          </div>
          <div className="flex items-center bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-1">
            {(['all', 'online', 'offline'] as const).map((f) => (
              <button
                key={f}
                onClick={() => setFilterMode(f)}
                className={cn(
                  "px-3 py-1.5 text-sm font-medium rounded-md transition-colors flex items-center gap-1.5",
                  filterMode === f
                    ? "bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300"
                    : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
                )}
              >
                {f === 'online' && <Circle className="w-2 h-2 fill-emerald-500 text-emerald-500" />}
                {f === 'offline' && <Circle className="w-2 h-2 fill-slate-400 text-slate-400" />}
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
        </div>
        
        {/* People List/Grid */}
        {isLoading ? (
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-violet-500 mb-3" />
            <p className="text-sm text-slate-400">Loading people...</p>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="text-center py-12 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
            <Users className="w-12 h-12 mx-auto text-slate-300 dark:text-slate-600 mb-4" />
            <p className="text-lg font-medium text-slate-700 dark:text-slate-300 mb-2">No people found</p>
            <p className="text-sm text-slate-400">
              {searchQuery ? 'Try a different search' : 'Team members will appear here'}
            </p>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredUsers.map((user) => (
              <PersonCardGrid key={user.id} user={user} onMessage={() => handleMessage(user.id)} />
            ))}
          </div>
        ) : (
          <div className="space-y-2">
            {filteredUsers.map((user) => (
              <PersonCardList key={user.id} user={user} onMessage={() => handleMessage(user.id)} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// Grid Card
function PersonCardGrid({ user, onMessage }: { user: User, onMessage: () => void }) {
  const presenceConfig = getPresenceConfig(user.is_online ? 'online' : 'offline')
  
  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5 hover:shadow-md transition-shadow">
      <div className="flex flex-col items-center text-center">
        <div className="relative mb-4">
          <div className={cn(
            "w-20 h-20 rounded-full flex items-center justify-center text-white text-2xl font-bold bg-gradient-to-br",
            getAvatarColor(user.name)
          )}>
            {getInitials(user.name, user.email)}
          </div>
          <div className={cn(
            "absolute bottom-1 right-1 w-5 h-5 rounded-full ring-4 ring-white dark:ring-slate-800",
            presenceConfig.color
          )} />
        </div>
        
        <h3 className="font-semibold text-slate-800 dark:text-white mb-1">{user.name}</h3>
        <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">{user.email}</p>
        {user.department && (
          <span className="text-xs px-2 py-1 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 mb-4">
            {user.department}
          </span>
        )}
        
        <div className="flex items-center gap-2 w-full mt-4">
          <button
            onClick={onMessage}
            className="flex-1 flex items-center justify-center gap-2 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg text-sm font-medium transition-colors"
          >
            <MessageSquare className="w-4 h-4" />
            Message
          </button>
          <button className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors">
            <Mail className="w-4 h-4 text-slate-500" />
          </button>
        </div>
      </div>
    </div>
  )
}

// List Card
function PersonCardList({ user, onMessage }: { user: User, onMessage: () => void }) {
  const presenceConfig = getPresenceConfig(user.is_online ? 'online' : 'offline')
  
  return (
    <div className="flex items-center gap-4 p-4 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 hover:shadow-md transition-shadow">
      <div className="relative">
        <div className={cn(
          "w-12 h-12 rounded-full flex items-center justify-center text-white text-lg font-semibold bg-gradient-to-br",
          getAvatarColor(user.name)
        )}>
          {getInitials(user.name, user.email)}
        </div>
        <div className={cn(
          "absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full ring-2 ring-white dark:ring-slate-800",
          presenceConfig.color
        )} />
      </div>
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-slate-800 dark:text-white truncate">{user.name}</h3>
          <span className={cn(
            "text-xs px-1.5 py-0.5 rounded",
            user.is_online 
              ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
              : "bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400"
          )}>
            {presenceConfig.label}
          </span>
        </div>
        <p className="text-sm text-slate-500 truncate">{user.email}</p>
        {user.custom_status && (
          <p className="text-xs text-slate-400 mt-0.5 truncate">{user.custom_status}</p>
        )}
      </div>
      
      <div className="flex items-center gap-2">
        <button
          onClick={onMessage}
          className="flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg text-sm font-medium transition-colors"
        >
          <MessageSquare className="w-4 h-4" />
          Message
        </button>
        <button className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors">
          <Mail className="w-4 h-4 text-slate-500" />
        </button>
      </div>
    </div>
  )
}
